/**
 * AxLabs Experiments - Chemical Equilibrium
 *
 * Demonstrates chemical equilibrium and Le Chatelier's principle.
 * Simulates the approach to equilibrium and response to perturbations.
 *
 * Governing equation: K = [C]^c[D]^d / [A]^a[B]^b
 */
import { BaseSimulationEngine, DIMENSIONS, rk4StepNumeric, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'initialA',
        name: 'Initial [A]',
        symbol: '[A]_0',
        unit: 'M',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0,
        max: 10,
        default: 1,
        step: 0.1,
        description: 'Initial concentration of reactant A',
    },
    {
        id: 'initialB',
        name: 'Initial [B]',
        symbol: '[B]_0',
        unit: 'M',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0,
        max: 10,
        default: 1,
        step: 0.1,
        description: 'Initial concentration of reactant B',
    },
    {
        id: 'initialC',
        name: 'Initial [C]',
        symbol: '[C]_0',
        unit: 'M',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0,
        max: 10,
        default: 0,
        step: 0.1,
        description: 'Initial concentration of product C',
    },
    {
        id: 'initialD',
        name: 'Initial [D]',
        symbol: '[D]_0',
        unit: 'M',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0,
        max: 10,
        default: 0,
        step: 0.1,
        description: 'Initial concentration of product D',
    },
    {
        id: 'kForward',
        name: 'Forward Rate Constant',
        symbol: 'k_f',
        unit: '1/(M·s)',
        dimension: { L: 3, M: 0, T: -1, I: 0, Θ: 0, N: -1, J: 0 },
        min: 0.01,
        max: 100,
        default: 1,
        step: 0.1,
        description: 'Rate constant for forward reaction',
    },
    {
        id: 'kReverse',
        name: 'Reverse Rate Constant',
        symbol: 'k_r',
        unit: '1/(M·s)',
        dimension: { L: 3, M: 0, T: -1, I: 0, Θ: 0, N: -1, J: 0 },
        min: 0.01,
        max: 100,
        default: 0.5,
        step: 0.1,
        description: 'Rate constant for reverse reaction',
    },
    {
        id: 'coeffA',
        name: 'Coefficient of A',
        symbol: 'a',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 3,
        default: 1,
        step: 1,
        description: 'Stoichiometric coefficient of A',
    },
    {
        id: 'coeffB',
        name: 'Coefficient of B',
        symbol: 'b',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 3,
        default: 1,
        step: 1,
        description: 'Stoichiometric coefficient of B',
    },
    {
        id: 'coeffC',
        name: 'Coefficient of C',
        symbol: 'c',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 3,
        default: 1,
        step: 1,
        description: 'Stoichiometric coefficient of C',
    },
    {
        id: 'coeffD',
        name: 'Coefficient of D',
        symbol: 'd',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 3,
        default: 1,
        step: 1,
        description: 'Stoichiometric coefficient of D',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'reaction',
        name: 'Reaction',
        latex: 'aA + bB \\rightleftharpoons cC + dD',
        variables: ['coeffA', 'coeffB', 'coeffC', 'coeffD'],
        description: 'Generic reversible reaction',
    },
    {
        id: 'equilibrium_constant',
        name: 'Equilibrium Constant',
        latex: 'K_{eq} = \\frac{k_f}{k_r} = \\frac{[C]^c[D]^d}{[A]^a[B]^b}',
        variables: ['kForward', 'kReverse', 'concC', 'concD', 'concA', 'concB'],
        description: 'Ratio of rate constants equals ratio of concentrations at equilibrium',
    },
    {
        id: 'reaction_quotient',
        name: 'Reaction Quotient',
        latex: 'Q = \\frac{[C]^c[D]^d}{[A]^a[B]^b}',
        variables: ['concC', 'concD', 'concA', 'concB'],
        description: 'Ratio of concentrations at any point (Q = K at equilibrium)',
    },
    {
        id: 'forward_rate',
        name: 'Forward Rate',
        latex: 'r_f = k_f[A]^a[B]^b',
        variables: ['kForward', 'concA', 'concB'],
        description: 'Rate of forward reaction',
    },
    {
        id: 'reverse_rate',
        name: 'Reverse Rate',
        latex: 'r_r = k_r[C]^c[D]^d',
        variables: ['kReverse', 'concC', 'concD'],
        description: 'Rate of reverse reaction',
    },
    {
        id: 'le_chatelier',
        name: 'Le Chatelier\'s Principle',
        latex: 'Q < K \\Rightarrow \\text{forward favored}; Q > K \\Rightarrow \\text{reverse favored}',
        variables: ['reactionQuotient'],
        description: 'System shifts to oppose changes and restore equilibrium',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'reactant_depleted',
        name: 'Reactant Depleted',
        description: 'A reactant has been completely consumed',
        check: (state) => {
            const A = state.variables['concA'] ?? 1;
            const B = state.variables['concB'] ?? 1;
            return A < 0.001 || B < 0.001;
        },
        severity: 'warning',
        educationalMessage: 'One of the reactants has been nearly completely consumed. ' +
            'The reaction is now limited by the availability of this reactant.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const CHEMICAL_EQUILIBRIUM_CONFIG = {
    id: 'chemical-equilibrium',
    slug: 'chemical-equilibrium',
    name: 'Chemical Equilibrium',
    domain: 'chemistry',
    description: 'Explore equilibrium dynamics and Le Chatelier\'s principle',
    longDescription: `
Chemical equilibrium occurs when the rates of forward and reverse reactions are equal,
resulting in constant concentrations of reactants and products.

Key concepts:
- Equilibrium is dynamic: reactions continue but rates are equal
- K = kf/kr = products/reactants at equilibrium
- Q vs K: determines reaction direction
- Le Chatelier's principle: system opposes perturbations

Equilibrium shifts:
- Adding reactant → shifts toward products
- Removing product → shifts toward products
- Temperature changes affect K

This simulation demonstrates:
- Approach to equilibrium from any starting point
- How Q compares to K over time
- Effect of changing concentrations
  `.trim(),
    version: 1,
    conceptTags: ['equilibrium', 'kinetics', 'le-chatelier', 'reaction-rates'],
    difficulty: 4,
    prerequisites: ['reaction-rates', 'stoichiometry'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'concA', type: 'parameter', parameterId: 'initialA' },
        { variableId: 'concB', type: 'parameter', parameterId: 'initialB' },
        { variableId: 'concC', type: 'parameter', parameterId: 'initialC' },
        { variableId: 'concD', type: 'parameter', parameterId: 'initialD' },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 0.01,
    maxDt: 0.1,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class ChemicalEquilibriumSimulation extends BaseSimulationEngine {
    config = CHEMICAL_EQUILIBRIUM_CONFIG;
    constructor() {
        super({ concA: 1, concB: 1, concC: 0, concD: 0, reactionQuotient: 0, atEquilibrium: 0 }, { type: 'rk4', dt: 0.01, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const A = params['initialA'] ?? 1;
        const B = params['initialB'] ?? 1;
        const C = params['initialC'] ?? 0;
        const D = params['initialD'] ?? 0;
        const Q = this.calculateQ(A, B, C, D, params);
        const K = this.getEquilibriumConstant();
        return {
            concA: A,
            concB: B,
            concC: C,
            concD: D,
            reactionQuotient: Q,
            atEquilibrium: Math.abs(Q - K) / K < 0.01 ? 1 : 0,
        };
    }
    /**
     * Calculate reaction quotient Q
     */
    calculateQ(A, B, C, D, params) {
        const a = params['coeffA'] ?? 1;
        const b = params['coeffB'] ?? 1;
        const c = params['coeffC'] ?? 1;
        const d = params['coeffD'] ?? 1;
        const numerator = Math.pow(Math.max(C, 1e-10), c) * Math.pow(Math.max(D, 1e-10), d);
        const denominator = Math.pow(Math.max(A, 1e-10), a) * Math.pow(Math.max(B, 1e-10), b);
        return numerator / denominator;
    }
    /**
     * Get equilibrium constant K
     */
    getEquilibriumConstant() {
        const kf = this.parameters['kForward'] ?? 1;
        const kr = this.parameters['kReverse'] ?? 0.5;
        return kf / kr;
    }
    computeDerivatives(state, params) {
        const kf = params['kForward'] ?? 1;
        const kr = params['kReverse'] ?? 0.5;
        const a = params['coeffA'] ?? 1;
        const b = params['coeffB'] ?? 1;
        const c = params['coeffC'] ?? 1;
        const d = params['coeffD'] ?? 1;
        // Forward rate: rf = kf * [A]^a * [B]^b
        const rf = kf * Math.pow(Math.max(state.concA, 0), a) * Math.pow(Math.max(state.concB, 0), b);
        // Reverse rate: rr = kr * [C]^c * [D]^d
        const rr = kr * Math.pow(Math.max(state.concC, 0), c) * Math.pow(Math.max(state.concD, 0), d);
        // Net rate
        const netRate = rf - rr;
        return {
            concA: -a * netRate,
            concB: -b * netRate,
            concC: c * netRate,
            concD: d * netRate,
        };
    }
    integrate(dt) {
        const derivative = (s, _t) => {
            const d = this.computeDerivatives(s, this.parameters);
            return {
                concA: d.concA ?? 0,
                concB: d.concB ?? 0,
                concC: d.concC ?? 0,
                concD: d.concD ?? 0,
                reactionQuotient: 0,
                atEquilibrium: 0,
            };
        };
        const newState = rk4StepNumeric(this.state, derivative, this.time, dt);
        // Ensure non-negative concentrations
        this.state.concA = Math.max(0, newState.concA ?? 0);
        this.state.concB = Math.max(0, newState.concB ?? 0);
        this.state.concC = Math.max(0, newState.concC ?? 0);
        this.state.concD = Math.max(0, newState.concD ?? 0);
        // Update Q and equilibrium status
        this.state.reactionQuotient = this.calculateQ(this.state.concA, this.state.concB, this.state.concC, this.state.concD, this.parameters);
        const K = this.getEquilibriumConstant();
        this.state.atEquilibrium = Math.abs(this.state.reactionQuotient - K) / K < 0.01 ? 1 : 0;
    }
    getPositionForStabilityCheck() {
        return this.state.reactionQuotient;
    }
    /**
     * Add concentration of a species (simulating addition)
     */
    addConcentration(species, amount) {
        switch (species) {
            case 'A':
                this.state.concA += amount;
                break;
            case 'B':
                this.state.concB += amount;
                break;
            case 'C':
                this.state.concC += amount;
                break;
            case 'D':
                this.state.concD += amount;
                break;
        }
    }
    /**
     * Get direction of reaction based on Q vs K
     */
    getReactionDirection() {
        const K = this.getEquilibriumConstant();
        const Q = this.state.reactionQuotient;
        if (Math.abs(Q - K) / K < 0.01) {
            return 'equilibrium';
        }
        return Q < K ? 'forward' : 'reverse';
    }
}
/**
 * Factory function to create a new chemical equilibrium simulation
 */
export function createChemicalEquilibrium() {
    return new ChemicalEquilibriumSimulation();
}
//# sourceMappingURL=chemical-equilibrium.js.map